import { Link } from "react-router-dom";
import { Confirmation, Success, Error } from "../../Components/Modal";
import { useMutation } from "react-query";
import { getCancellationReasons, handleCancellationReasonStatus, deleteCancellationReason } from "../../Services/CancellationReason";
import useTableControls from "../../Hooks/useTableControls";
import useFetchData from "../../Hooks/useFetchData";
import Pagination from "../../Components/Table/Pagination";
import PageHeader from "../../Components/PageHeader";
import "../../styles/PremiumCardPages.css";

export default function CancellationManagement() {
    const {
        perPage,
        status,
        search_string,
        from,
        to,
    } = useTableControls();

    const {
        page,
        setPage,
        isLoading,
        data,
        refetch,
    } = useFetchData("cancellation_reasons", getCancellationReasons, [
        perPage,
        search_string,
        status,
        from,
        to,
    ]);

    const reasons = data?.data?.data?.docs || [];

    const { mutate: mutateStatus } = useMutation(
        (id) => handleCancellationReasonStatus(id),
        {
            onSuccess: (res) => {
                Success("Status Updated", res.data?.message || "Status updated successfully.");
                refetch();
            },
            onError: (err) => {
                Error(err?.response?.data?.message || "Something went wrong.");
            },
        }
    );

    const { mutate: mutateDelete } = useMutation(
        (id) => deleteCancellationReason(id),
        {
            onSuccess: (res) => {
                Success("Deleted", res.data?.message || "Removed successfully.");
                refetch();
            },
            onError: (err) => {
                Error(err?.response?.data?.message || "Something went wrong.");
            },
        }
    );

    const handleDelete = (id, reason) => {
        Confirmation(
            `Are you sure you want to delete "${reason}"?`,
            "Yes, Delete",
            () => mutateDelete(id)
        );
    };

    const toggleStatus = (id, reason, currentStatus) => {
        Confirmation(
            `Are you sure you want to ${currentStatus ? "deactivate" : "activate"} "${reason}"?`,
            "Yes, Update",
            () => mutateStatus(id)
        );
    };

    return (
        <div className="app-content content">
            <div className="content-wrapper">
                <div className="content-body">
                    <section id="cancellation-management">
                        <div className="user-details-wrapper">
                            <PageHeader
                                title="Cancellation Reasons"
                                subtitle="Manage reasons for users and drivers to cancel bookings"
                                action={
                                    <Link to="/cancellation-management/add" className="pcard-add-btn">
                                        <i className="fas fa-plus me-2" />
                                        Add New Reason
                                    </Link>
                                }
                            />

                            <div className="pcard-grid">
                                {isLoading && reasons.length === 0 ? (
                                    <div className="pcard-loading">
                                        <div className="spinner-border text-warning" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                    </div>
                                ) : reasons.length > 0 ? (
                                    reasons.map((item) => {
                                        const isDriver = item.userType === "driver";
                                        return (
                                            <article
                                                key={item._id}
                                                className={`reason-card-v2 ${isDriver ? "reason-card-v2--driver" : "reason-card-v2--user"}`}
                                            >
                                                <div className="reason-card-v2__head">
                                                    <span className={`reason-card-v2__badge ${isDriver ? "reason-card-v2__badge--driver" : "reason-card-v2__badge--user"}`}>
                                                        <i className={`fas ${isDriver ? "fa-truck" : "fa-user"}`} />
                                                        {isDriver ? "Driver" : "User"}
                                                    </span>
                                                    <div className="dropdown pcard-dropdown">
                                                        <button
                                                            type="button"
                                                            className="pcard-menu-btn"
                                                            data-bs-toggle="dropdown"
                                                            aria-label="Reason actions"
                                                        >
                                                            <i className="fas fa-ellipsis-h" />
                                                        </button>
                                                        <ul className="dropdown-menu dropdown-menu-end">
                                                            <li>
                                                                <Link
                                                                    className="dropdown-item"
                                                                    to={`/cancellation-management/edit/${item._id}`}
                                                                >
                                                                    <i className="fas fa-edit me-2 text-primary" />
                                                                    Edit
                                                                </Link>
                                                            </li>
                                                            <li>
                                                                <button
                                                                    type="button"
                                                                    className="dropdown-item text-danger"
                                                                    onClick={() => handleDelete(item._id, item.reason)}
                                                                >
                                                                    <i className="fas fa-trash-alt me-2" />
                                                                    Delete
                                                                </button>
                                                            </li>
                                                        </ul>
                                                    </div>
                                                </div>

                                                <p className="reason-card-v2__text">{item.reason}</p>

                                                <div className="reason-card-v2__footer">
                                                    <button
                                                        type="button"
                                                        className={`reason-card-v2__status ${item.status ? "reason-card-v2__status--active" : "reason-card-v2__status--inactive"}`}
                                                        onClick={() => toggleStatus(item._id, item.reason, item.status)}
                                                    >
                                                        <span className="reason-card-v2__status-dot" />
                                                        {item.status ? "Active" : "Inactive"}
                                                    </button>
                                                    <Link
                                                        to={`/cancellation-management/edit/${item._id}`}
                                                        className="reason-card-v2__edit"
                                                    >
                                                        <i className="fas fa-pen" />
                                                        Edit
                                                    </Link>
                                                </div>
                                            </article>
                                        );
                                    })
                                ) : (
                                    <div className="pcard-empty">
                                        <div className="pcard-empty__icon">
                                            <i className="fas fa-ban" />
                                        </div>
                                        <h3 className="pcard-empty__title">No reasons found</h3>
                                        <p className="pcard-empty__text">Create cancellation reasons for users and drivers.</p>
                                    </div>
                                )}
                            </div>

                            <div className="pcard-pagination">
                                <Pagination
                                    currentDocs={reasons.length}
                                    totalDocs={data?.data?.data?.totalDocs}
                                    totalPages={Math.ceil(data?.data?.data?.totalPages || 0)}
                                    page={page}
                                    setPage={setPage}
                                />
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
}
